import { useTranslation } from 'react-i18next';
import { ColumnDef } from '@tanstack/react-table';
import { CampaignPostSortField } from '@anytag/graphql';
import { useInfluencerPermissions } from '@anytag/auth';
import { TableCell, ColumnCustomiser } from '@anytag/shared-atoms';
import { usePostDetailsParam, useToggleTableColumns } from '@anytag/shared-hooks';
import { formatIntNumber, formatNumber, formatPercent, formatPrice } from '@anytag/shared-utils';
import { PostColumn } from '../post-column';
import { CampaignPostsDiscoveryRowData, PostDiscoveryStatsKey } from '../types';

export interface UseAllCampaignPostsListTableProps {
  total?: number;
  currencyId?: string;
}

export const useAllCampaignPostsListTable = ({ total, currencyId }: UseAllCampaignPostsListTableProps) => {
  const { t } = useTranslation();
  const { setPostDetailsParam } = usePostDetailsParam();
  const { hideViewInfluencerProfile } = useInfluencerPermissions();
  const { getSelectableColumns, handleToggleColumns } = useToggleTableColumns<
    CampaignPostsDiscoveryRowData,
    PostDiscoveryStatsKey
  >();

  const columns: ColumnDef<CampaignPostsDiscoveryRowData>[] = [
    {
      enableHiding: false,
      accessorKey: 'title',
      meta: { sticky: true },
      header: ({ table }) => {
        const { selectableOptions } = getSelectableColumns(table);

        return (
          <div className="flex items-center justify-between gap-2">
            {t<string>('HeaderColumn.Total Post')}
            <ColumnCustomiser
              options={selectableOptions}
              onChange={handleToggleColumns(table)}
              savedValuesKey="postDiscoveryTableColumns"
              buttonProps={{
                tooltip: { place: 'top', help: <span className="text-sm">{t('Customize metrics')}</span> }
              }}
            />
          </div>
        );
      },
      cell: ({ row }) => {
        const { className, influencerInfo } = row.original;

        return className === 'total-row' ? (
          <TableCell.CampaignTotal count={total ?? 0} />
        ) : className === 'average-row' ? (
          <TableCell.CampaignAverage title={t('Average')} />
        ) : influencerInfo ? (
          <PostColumn
            avatar={influencerInfo.avatar}
            content={influencerInfo.content}
            thumbnail={influencerInfo.thumbnail || ''}
            influencerId={influencerInfo.influencerId}
            campaignName={influencerInfo.campaignName}
            influencerName={influencerInfo.influencerName}
            socialPostType={influencerInfo.socialPostType}
            isBlockedProfileLink={hideViewInfluencerProfile}
            campaignIdentifier={influencerInfo.campaignIdentifier}
            onPostClick={() => {
              if (influencerInfo?.id) {
                setPostDetailsParam(influencerInfo.id);
              }
            }}
          />
        ) : null;
      }
    },
    {
      accessorKey: 'cost',
      enableHiding: false,
      header: t<string>('HeaderColumn.Cost'),
      meta: { align: 'right', sortField: CampaignPostSortField.COST },
      cell: ({ row }) => (
        <TableCell.Number
          unit={row.original.stats?.cost ? currencyId : ''}
          value={formatPrice(row.original.stats?.cost, currencyId)}
        />
      )
    },
    {
      accessorKey: 'followers',
      enableHiding: false,
      header: t<string>('HeaderColumn.Follower'),
      meta: { align: 'right', sortField: CampaignPostSortField.FOLLOWERS },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.followers)} />
    },
    {
      accessorKey: 'impressions',
      header: t<string>('HeaderColumn.Impression'),
      meta: { align: 'right', sortField: CampaignPostSortField.IMPRESSIONS },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.impressions)} />
    },
    {
      accessorKey: 'impressionsRate',
      header: t<string>('HeaderColumn.Impression Rate'),
      meta: { align: 'right', sortField: CampaignPostSortField.IMPRESSIONS_RATE },
      cell: ({ row }) => <TableCell.Number value={formatPercent(row.original.stats?.impressionsRate, false)} unit="%" />
    },
    {
      accessorKey: 'impressionsFromDiscovery',
      header: t<string>('HeaderColumn.Impression from Discovery'),
      meta: { align: 'right', sortField: CampaignPostSortField.IMPRESSIONS_FROM_DISCOVERY },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.impressionsFromDiscovery)} />
    },
    {
      accessorKey: 'impressionsFromHashtag',
      header: t<string>('HeaderColumn.Impression from Hashtag'),
      meta: { align: 'right', sortField: CampaignPostSortField.IMPRESSIONS_FROM_HASHTAG },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.impressionsFromHashtag)} />
    },
    {
      accessorKey: 'impressionsFromHome',
      header: t<string>('HeaderColumn.Impression from Home'),
      meta: { align: 'right', sortField: CampaignPostSortField.IMPRESSIONS_FROM_HOME },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.impressionsFromHome)} />
    },
    {
      accessorKey: 'impressionsFromProfile',
      header: t<string>('HeaderColumn.Impression from Profile'),
      meta: { align: 'right', sortField: CampaignPostSortField.IMPRESSIONS_FROM_PROFILE },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.impressionsFromProfile)} />
    },
    {
      accessorKey: 'impressionsFromOther',
      header: t<string>('HeaderColumn.Impression from Other'),
      meta: { align: 'right', sortField: CampaignPostSortField.IMPRESSIONS_FROM_OTHER },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.impressionsFromOther)} />
    },
    {
      accessorKey: 'impressionsClickThroughRate',
      header: t<string>('HeaderColumn.Impressions Click Through Rate'),
      meta: { align: 'right', sortField: CampaignPostSortField.IMPRESSIONS_CLICK_THROUGH_RATE },
      cell: ({ row }) => (
        <TableCell.Number value={formatPercent(row.original.stats?.impressionsClickThroughRate, false)} unit="%" />
      )
    },
    {
      accessorKey: 'totalPlayTime',
      header: t<string>('HeaderColumn.Total play time'),
      meta: { align: 'right', sortField: CampaignPostSortField.TOTAL_PLAY_TIME },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.totalPlayTime)} />
    },
    {
      accessorKey: 'averageViewTime',
      header: t<string>('HeaderColumn.Average view time'),
      meta: { align: 'right', sortField: CampaignPostSortField.AVERAGE_VIEW_TIME },
      cell: ({ row }) => <TableCell.Number value={formatNumber(row.original.stats?.averageViewTime, 2)} />
    },
    {
      accessorKey: 'videoCompletionRate',
      header: t<string>('HeaderColumn.Video completion rate'),
      meta: { align: 'right', sortField: CampaignPostSortField.VIDEO_COMPLETION_RATE },
      cell: ({ row }) => (
        <TableCell.Number value={formatPercent(row.original.stats?.videoCompletionRate, false)} unit="%" />
      )
    },
    {
      accessorKey: 'views',
      header: t<string>('HeaderColumn.Video view'),
      meta: { align: 'right', sortField: CampaignPostSortField.VIEWS },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.views)} />
    },
    {
      accessorKey: 'averageViewDurationSeconds',
      header: t<string>('HeaderColumn.Average View Duration (m)'),
      meta: { align: 'right', sortField: CampaignPostSortField.AVERAGE_VIEW_DURATION_SECONDS },
      cell: ({ row }) => (
        <TableCell.Number value={formatIntNumber(row.original.stats?.averageViewDurationSeconds)} unit="s" />
      )
    },
    {
      accessorKey: 'averageViewDurationPercentage',
      header: t<string>('HeaderColumn.Average View Duration Rate'),
      meta: { align: 'right', sortField: CampaignPostSortField.AVERAGE_VIEW_DURATION_PERCENTAGE },
      cell: ({ row }) => (
        <TableCell.Number value={formatPercent(row.original.stats?.averageViewDurationPercentage, false)} unit="%" />
      )
    },
    {
      accessorKey: 'reach',
      header: t<string>('HeaderColumn.Reach'),
      meta: { align: 'right', sortField: CampaignPostSortField.REACH },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.reach)} />
    },
    {
      accessorKey: 'reachRate',
      header: t<string>('HeaderColumn.Reach Rate'),
      meta: { align: 'right', sortField: CampaignPostSortField.REACH_RATE },
      cell: ({ row }) => <TableCell.Number value={formatPercent(row.original.stats?.reachRate, false)} unit="%" />
    },
    {
      accessorKey: 'nonFollowerReach',
      header: t<string>('HeaderColumn.Non Follower Reach'),
      meta: { align: 'right', sortField: CampaignPostSortField.NON_FOLLOWER_REACH },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.nonFollowerReach)} />
    },
    {
      accessorKey: 'nonFollowerReachRate',
      header: t<string>('HeaderColumn.Non Follower Reach Rate'),
      meta: { align: 'right', sortField: CampaignPostSortField.NON_FOLLOWER_REACH_RATE },
      cell: ({ row }) => (
        <TableCell.Number value={formatPercent(row.original.stats?.nonFollowerReachRate, false)} unit="%" />
      )
    },
    {
      accessorKey: 'engagement',
      enableHiding: false,
      header: t<string>('HeaderColumn.Engagement'),
      meta: { align: 'right', sortField: CampaignPostSortField.ENGAGEMENT },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.engagement)} />
    },
    {
      accessorKey: 'engagementFollowersRate',
      enableHiding: false,
      header: t<string>('HeaderColumn.Egg Rate by followers'),
      meta: { align: 'right', sortField: CampaignPostSortField.ENGAGEMENT_FOLLOWERS_RATE },
      cell: ({ row }) => (
        <TableCell.Number value={formatPercent(row.original.stats?.engagementFollowersRate, false)} unit="%" />
      )
    },
    {
      accessorKey: 'engagementViewsRate',
      enableHiding: false,
      header: t<string>('HeaderColumn.Egg Rate by views'),
      meta: { align: 'right', sortField: CampaignPostSortField.ENGAGEMENT_VIEWS_RATE },
      cell: ({ row }) => (
        <TableCell.Number value={formatPercent(row.original.stats?.engagementViewsRate, false)} unit="%" />
      )
    },
    {
      accessorKey: 'likes',
      enableHiding: false,
      header: t<string>('HeaderColumn.Like'),
      meta: { align: 'right', sortField: CampaignPostSortField.LIKES },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.likes)} />
    },
    {
      accessorKey: 'dislikes',
      header: t<string>('HeaderColumn.Dislike'),
      meta: { align: 'right', sortField: CampaignPostSortField.DISLIKES },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.dislikes)} />
    },
    {
      accessorKey: 'comments',
      header: t<string>('HeaderColumn.Comment'),
      meta: { align: 'right', sortField: CampaignPostSortField.COMMENTS },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.comments)} />
    },
    {
      accessorKey: 'shares',
      header: t<string>('HeaderColumn.Share'),
      meta: { align: 'right', sortField: CampaignPostSortField.SHARES },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.shares)} />
    },
    {
      accessorKey: 'saved',
      header: t<string>('HeaderColumn.Save'),
      meta: { align: 'right', sortField: CampaignPostSortField.SAVED },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.saved)} />
    },
    {
      accessorKey: 'interaction',
      header: t<string>('HeaderColumn.Interaction'),
      meta: { align: 'right', sortField: CampaignPostSortField.INTERACTION },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.interaction)} />
    },
    {
      accessorKey: 'linkClicks',
      header: t<string>('HeaderColumn.Link Clicks'),
      meta: { align: 'right', sortField: CampaignPostSortField.LINK_CLICKS },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.linkClicks)} />
    },
    {
      accessorKey: 'stickerTaps',
      header: t<string>('HeaderColumn.Sticker Taps'),
      meta: { align: 'right', sortField: CampaignPostSortField.STICKER_TAPS },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.stickerTaps)} />
    },
    {
      accessorKey: 'clicks',
      header: t<string>('HeaderColumn.Click'),
      meta: { align: 'right', sortField: CampaignPostSortField.CLICKS },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.clicks)} />
    },
    {
      accessorKey: 'conversions',
      header: t<string>('HeaderColumn.Conversion'),
      meta: { align: 'right', sortField: CampaignPostSortField.CONVERSIONS },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.conversions)} />
    },
    {
      accessorKey: 'revenue',
      header: t<string>('HeaderColumn.Revenue'),
      meta: { align: 'right', sortField: CampaignPostSortField.REVENUE },
      cell: ({ row }) => (
        <TableCell.Number value={formatPrice(row.original.stats?.revenue, currencyId)} unit={currencyId} />
      )
    },
    {
      accessorKey: 'orders',
      header: t<string>('HeaderColumn.Orders'),
      meta: { align: 'right', sortField: CampaignPostSortField.ORDERS },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.orders)} />
    },
    {
      accessorKey: 'sales',
      header: t<string>('HeaderColumn.Sales'),
      meta: { align: 'right', sortField: CampaignPostSortField.SALES },
      cell: ({ row }) => (
        <TableCell.Number value={formatPrice(row.original.stats?.sales, currencyId)} unit={currencyId} />
      )
    },
    {
      accessorKey: 'salesCost',
      header: t<string>('HeaderColumn.Sales Cost'),
      meta: { align: 'right', sortField: CampaignPostSortField.SALES_COST },
      cell: ({ row }) => (
        <TableCell.Number value={formatPrice(row.original.stats?.salesCost, currencyId)} unit={currencyId} />
      )
    },
    {
      accessorKey: 'salesReferral',
      header: t<string>('HeaderColumn.Sales (Referral)'),
      meta: { align: 'right', sortField: CampaignPostSortField.SALES_REFERRAL },
      cell: ({ row }) => (
        <TableCell.Number value={formatPrice(row.original.stats?.salesReferral, currencyId)} unit={currencyId} />
      )
    },
    {
      accessorKey: 'femalePercentage',
      header: t<string>('HeaderColumn.Female Rate'),
      meta: { align: 'right', sortField: CampaignPostSortField.FEMALE_PERCENTAGE },
      cell: ({ row }) => (
        <TableCell.Number value={formatPercent(row.original.stats?.femalePercentage, false)} unit="%" />
      )
    },
    {
      accessorKey: 'malePercentage',
      header: t<string>('HeaderColumn.Male Rate'),
      meta: { align: 'right', sortField: CampaignPostSortField.MALE_PERCENTAGE },
      cell: ({ row }) => <TableCell.Number value={formatPercent(row.original.stats?.malePercentage, false)} unit="%" />
    },
    {
      accessorKey: 'age18to24Percentage',
      header: t<string>('HeaderColumn.Age (18-24)'),
      meta: { align: 'right', sortField: CampaignPostSortField.AGE_18_TO_24_PERCENTAGE },
      cell: ({ row }) => (
        <TableCell.Number value={formatPercent(row.original.stats?.age18to24Percentage, false)} unit="%" />
      )
    },
    {
      accessorKey: 'age25to34Percentage',
      header: t<string>('HeaderColumn.Age (25-34)'),
      meta: { align: 'right', sortField: CampaignPostSortField.AGE_25_TO_34_PERCENTAGE },
      cell: ({ row }) => (
        <TableCell.Number value={formatPercent(row.original.stats?.age25to34Percentage, false)} unit="%" />
      )
    },
    {
      accessorKey: 'age35upPercentage',
      header: t<string>('HeaderColumn.Age (35+)'),
      meta: { align: 'right', sortField: CampaignPostSortField.AGE_35_UP_PERCENTAGE },
      cell: ({ row }) => (
        <TableCell.Number value={formatPercent(row.original.stats?.age35upPercentage, false)} unit="%" />
      )
    },
    {
      accessorKey: 'back',
      header: t<string>('HeaderColumn.Back'),
      meta: { align: 'right', sortField: CampaignPostSortField.BACK },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.back)} />
    },
    {
      accessorKey: 'exited',
      header: t<string>('HeaderColumn.Exited'),
      meta: { align: 'right', sortField: CampaignPostSortField.EXITED },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.exited)} />
    },
    {
      accessorKey: 'forward',
      header: t<string>('HeaderColumn.Forward'),
      meta: { align: 'right', sortField: CampaignPostSortField.FORWARD },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.forward)} />
    },
    {
      accessorKey: 'nextStory',
      header: t<string>('HeaderColumn.Next Story'),
      meta: { align: 'right', sortField: CampaignPostSortField.NEXT_STORY },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.nextStory)} />
    },
    {
      accessorKey: 'navigation',
      header: t<string>('HeaderColumn.Navigation'),
      meta: { align: 'right', sortField: CampaignPostSortField.NAVIGATION },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.navigation)} />
    },
    {
      accessorKey: 'profileActivity',
      header: t<string>('HeaderColumn.Profile Activity'),
      meta: { align: 'right', sortField: CampaignPostSortField.PROFILE_ACTIVITY },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.profileActivity)} />
    },
    {
      accessorKey: 'audienceRetention',
      header: t<string>('HeaderColumn.Audience Retention'),
      meta: { align: 'right', sortField: CampaignPostSortField.AUDIENCE_RETENTION },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.audienceRetention)} />
    }
  ];

  return { columns };
};
